import React, { useEffect, useState } from 'react';
import { Button, Card, Table, Tag, App, Switch, Typography, Row } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { QualifyingFlowQuestion, QuestionType } from '../../../../dto/qualifying-flow-models';
import { getQualifyingQuestionsAdmin, postQualifyingQuestion, putQualifyingQuestion } from '../../../../services/admin-page-service';
import { GetAccessToken } from '../../../../utils/auth-utils';
import { useMsal } from '@azure/msal-react';
import { scopes } from '../../../../authConfig';
import { QualifyingQuestionForm } from './qualifying-question-form';

const { Title, Text } = Typography;

export const QualifyingQuestionsComponent = () => {
  const { instance, inProgress } = useMsal();
  const { notification } = App.useApp();
  const [questions, setQuestions] = useState<QualifyingFlowQuestion[]>([]);
  const [editingQuestion, setEditingQuestion] = useState<QualifyingFlowQuestion | null>(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const result = await getQualifyingQuestionsAdmin(token.accessToken);
    if (result.isSuccess) {
      setQuestions(result.data ?? []);
    }
  };

  const handleAddNew = () => {
    const newQuestion: QualifyingFlowQuestion = {
      id: '',
      feId: crypto.randomUUID(),
      questionKey: '',
      questionText: { de: '' },
      questionType: QuestionType.Dropdown,
      options: [],
      sortOrder: questions.length + 1,
      isActive: true,
    };
    setEditingQuestion(newQuestion);
    setShowForm(true);
  };

  const handleEdit = (q: QualifyingFlowQuestion) => {
    setEditingQuestion({ ...q });
    setShowForm(true);
  };

  const handleSubmit = async (question: QualifyingFlowQuestion) => {
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      console.log('Submitting question', question);
      const result =
        question.id === ''
          ? await postQualifyingQuestion(token.accessToken, question)
          : await putQualifyingQuestion(token.accessToken, question);

      if (result.isSuccess) {
        notification.success({ message: 'Question saved successfully' });
        setShowForm(false);
        setEditingQuestion(null);
        await fetchQuestions();
      } else {
        notification.error({
          message: 'Failed to save question',
          description: result.errors?.join(', ') || 'Unknown error',
        });
      }
    } catch (e: any) {
      notification.error({ message: 'Failed to save question', description: e.message });
    }
  };

  const handleToggleActive = async (question: QualifyingFlowQuestion, isActive: boolean) => {
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const result = await putQualifyingQuestion(token.accessToken, { ...question, isActive });
      if (result.isSuccess) {
        await fetchQuestions();
      } else {
        notification.error({ message: 'Failed to update question status' });
      }
    } catch (e: any) {
      notification.error({ message: 'Failed to update question status', description: e.message });
    }
  };

  const columns = [
    {
      title: 'Key',
      dataIndex: 'questionKey',
      key: 'questionKey',
      width: 220,
      render: (key: string) => <code>{key}</code>,
    },
    {
      title: 'Text (DE)',
      dataIndex: 'questionText',
      key: 'questionText',
      render: (text: { [k: string]: string }) => text?.de ?? '',
    },
    {
      title: 'Text (EN)',
      dataIndex: 'questionText',
      key: 'questionText',
      render: (text: { [k: string]: string }) => text?.en ?? '',
    },
    {
      title: 'Type',
      dataIndex: 'questionType',
      key: 'questionType',
      width: 140,
      render: (type: QuestionType) => (
        <Tag color={type === QuestionType.Dropdown ? 'blue' : 'purple'}>{type}</Tag>
      ),
    },
    {
      title: 'Options',
      dataIndex: 'options',
      key: 'options',
      width: 80,
      render: (opts: any[]) => opts?.length ?? 0,
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      render: (isActive: boolean, record: QualifyingFlowQuestion) => (
        <Switch checked={isActive} onChange={(val) => handleToggleActive(record, val)} />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_: any, record: QualifyingFlowQuestion) => (
        <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div className="container flex-admin-vertical-container" style={{ width: 900, justifyContent: 'flex-start' }}>
      <Row style={{ marginBottom: '8px', flexDirection: 'column' }}>
        <Title level={4} style={{ marginBottom: 0 }}>
          Qualifying Questions
        </Title>
        <Text type="secondary" style={{ display: 'block', marginTop: '5px' }}>
          Manage the library of qualifying questions. Questions can be assigned to qualification flows.
        </Text>
      </Row>

      {showForm && editingQuestion ? (
        <Card style={{ marginBottom: 24 }}>
          <Title level={5}>{editingQuestion.id === '' ? 'New Question' : 'Edit Question'}</Title>
          <QualifyingQuestionForm
            question={editingQuestion}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingQuestion(null);
            }}
          />
        </Card>
      ) : (
        <Row justify="end" style={{ marginBottom: 16, marginTop: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
            Add Question
          </Button>
        </Row>
      )}

      <Table
        dataSource={questions}
        columns={columns}
        rowKey="feId"
        size="small"
        pagination={{ pageSize: 20 }}
        style={{ width: '100%' }}
      />
    </div>
  );
};
